import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getProjectsAndBotsAPI } from '../../services/api';

// Which project/bot the thread runs against. Lives here rather than in the
// sidebar because the composer needs it too: no project + bot, no sending.

const SelectionContext = createContext(null);

// GET /api/v1/chat/bots answers
//   { data: { projects: [{ name, title, image_llm, bots: [...] }], limits: {...} } }
// so the bot list and the composer's limits both arrive here and there is no
// second config call to make.
//
// Every API response is wrapped in `data` by the server's writeJSONWithStatus,
// so unwrap that first. The array branch is the older shape, when this endpoint
// answered a bare list.
const body = (response) => response?.data ?? response;

const toProjects = (response) => {
  const b = body(response);
  if (Array.isArray(b)) return b;
  if (Array.isArray(b?.projects)) return b.projects;
  return [];
};

const titleOf = (entry) => entry?.title || entry?.name || '';

// What the server will accept. The defaults only cover the moment before the
// first fetch lands; the server enforces its own numbers regardless.
const DEFAULT_LIMITS = { maxFileBytes: 0, maxImageBytes: 0, maxFilesPerMessage: 10 };

const toLimits = (response) => {
  const limits = body(response)?.limits;
  if (!limits) return DEFAULT_LIMITS;

  return {
    maxFileBytes: limits.max_file_bytes ?? 0,
    maxImageBytes: limits.max_image_bytes ?? 0,
    maxFilesPerMessage: limits.max_files_per_message || DEFAULT_LIMITS.maxFilesPerMessage,
  };
};

export function SelectionProvider({ children }) {
  const [projects, setProjects] = useState([]);
  const [limits, setLimits] = useState(DEFAULT_LIMITS);
  const [project, setProjectName] = useState('');
  const [bot, setBot] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await getProjectsAndBotsAPI();
      const data = toProjects(response);
      setProjects(data);
      setLimits(toLimits(response));

      // one of a thing is not a choice, so make it for them
      if (data.length === 1) {
        setProjectName(data[0].name);
        if (data[0].bots?.length === 1) {
          setBot(data[0].bots[0].name);
        }
      }
    } catch (err) {
      console.error('Failed to load projects and bots:', err);
      setError(err?.message || 'Failed to load projects and bots.');
      setProjects([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const bots = useMemo(
    () => projects.find((p) => p.name === project)?.bots ?? [],
    [projects, project],
  );

  const setProject = useCallback(
    (name) => {
      setProjectName(name);

      // a bot belongs to a project, so the old pick can't survive the switch
      const next = projects.find((p) => p.name === name)?.bots ?? [];
      setBot(next.length === 1 ? next[0].name : '');
    },
    [projects],
  );

  const value = useMemo(
    () => ({
      projects,
      bots,
      project,
      bot,
      projectTitle: titleOf(projects.find((p) => p.name === project)),
      botTitle: titleOf(bots.find((b) => b.name === bot)),
      setProject,
      setBot,
      canSend: Boolean(project) && Boolean(bot),
      // The selected project has a model that can read a picture. Empty for
      // most projects, and then the composer must not offer image uploads: the
      // file would be stored and then ignored, which reads as the chat losing
      // it.
      canSendImages: Boolean(projects.find((p) => p.name === project)?.image_llm),
      limits,
      isLoading,
      error,
      reload: load,
    }),
    [projects, bots, project, bot, setProject, limits, isLoading, error, load],
  );

  return <SelectionContext.Provider value={value}>{children}</SelectionContext.Provider>;
}

export function useSelection() {
  const context = useContext(SelectionContext);
  if (!context) {
    throw new Error('useSelection must be used within a SelectionProvider');
  }
  return context;
}
